import { jsx as _jsx } from "react/jsx-runtime";
/**
 * The states every feedback component speaks (ADR-0088 D2): a Notice, a
 * Toast and a Badge all take one of these, named for what the message means,
 * not for its colour, so a later colour decision changes one place.
 *
 * - `neutral`: orientation or information; nothing is wrong.
 * - `success`: a confirmed, positive outcome.
 * - `warning`: a state that deserves attention before the next action.
 * - `danger`: a failure that must be read.
 *
 * A status is never carried by colour alone: Notice and Toast draw the icon
 * below for their state, and a Badge's text is its status.
 *
 * Each state has one look, below, that all three use, so they cannot drift
 * apart: a tint (border and fill) for a notice, a badge and a toast's icon
 * tile, and a foreground for icons and a badge's text. A notice's own text
 * stays primary, for the longer message it carries.
 */
import { Icon } from './icon.js';
import { cn } from './cn.js';
const NEUTRAL_EDGE = '[--feedback-border:var(--color-border-default)] [--feedback-action-hover:color-mix(in_oklab,var(--color-border-default)_65%,var(--color-surface-app))] border-[var(--feedback-border)]';
/** Each state's tint for notices, badges and toast icon tiles: the border
 * and the fill. Neutral sits on the plain surface. The borders' strengths
 * differ so each reads at the same weight. */
export const FEEDBACK_TINT = {
    neutral: `${NEUTRAL_EDGE} bg-surface-app`,
    success: '[--feedback-border:color-mix(in_oklab,var(--color-success)_30%,transparent)] [--feedback-action-hover:color-mix(in_oklab,var(--color-success)_15%,transparent)] border-[var(--feedback-border)] bg-success/10',
    warning: '[--feedback-border:color-mix(in_oklab,var(--color-warning)_50%,transparent)] [--feedback-action-hover:color-mix(in_oklab,var(--color-warning)_30%,transparent)] border-[var(--feedback-border)] bg-warning/10',
    danger: '[--feedback-border:color-mix(in_oklab,var(--color-danger)_40%,transparent)] [--feedback-action-hover:color-mix(in_oklab,var(--color-danger)_20%,transparent)] border-[var(--feedback-border)] bg-danger/10',
};
/** A neutral notice placed directly on the app canvas (`surface="app"`) takes
 * the raised card surface, so its fill stays distinct from the page. */
export const NEUTRAL_TINT_ON_APP = `${NEUTRAL_EDGE} bg-surface-card`;
/** Each state's foreground, for icons and a badge's text: the state's own
 * hue, dark enough to read on its tint (WCAG AA for 12px text). */
export const FEEDBACK_FOREGROUND = {
    neutral: 'text-text-secondary',
    success: 'text-success-text',
    warning: 'text-warning-text',
    danger: 'text-danger-text',
};
const FEEDBACK_ICON = {
    neutral: 'info',
    success: 'success',
    warning: 'warning',
    danger: 'danger',
};
/** The state's icon, decorative: the message beside it carries the meaning. */
export function FeedbackIcon({ variant, size, className, }) {
    return _jsx(Icon, { name: FEEDBACK_ICON[variant], size: size, "data-icon": variant, className: cn('shrink-0', className) });
}
//# sourceMappingURL=feedback.js.map